import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { ArrowRight, Building, History, Users2, Building2, KeyRound } from 'lucide-react';
import { useHomePageData } from '../hooks/useHomePageData';

const HomePage = () => {
  const { user } = useAuth();
  const { stats, loading, error, refetch } = useHomePageData(user?.role || null);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-32 bg-gray-200 rounded animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6 pt-2">
        <div className="text-center">
          <div className="text-red-600 mb-4">
            <p className="text-lg font-semibold">Failed to load dashboard data</p>
            <p className="text-sm">{error}</p>
          </div>
          <Button onClick={refetch} className="bg-red-600 hover:bg-red-700">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pt-2">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Dashboard</h1>
        <p className="text-muted-foreground">Benvenuto, {user?.displayName || user?.email}! Clicca su Riconciliazione bonifici per iniziare la riconciliazione dei tuoi stabili.</p>
      </div>



      {/* Quick Actions */}
      <div className="space-y-6">
        {/* Admin Section */}
        {user?.role === 'admin' && (
          <div>
            <h2 className="text-xl font-semibold mb-4 text-gray-800">Admin Management</h2>
            <div className="grid gap-6 md:grid-cols-2">
              <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200 hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-indigo-800">
                    <Users2 className="h-5 w-5" />
                    User Management
                  </CardTitle>
                  <CardDescription className="text-indigo-700">
                    Manage users, roles, and permissions.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <Button asChild className="bg-indigo-600 hover:bg-indigo-700">
                      <Link to="/admin/users">
                        Manage Users <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-indigo-900">{stats.totalUsers}</div>
                      <p className="text-xs text-indigo-700">
                        {stats.activeUsers} active, {stats.inactiveUsers} inactive
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200 hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-emerald-800">
                    <Building className="h-5 w-5" />
                    Organizations
                  </CardTitle>
                  <CardDescription className="text-emerald-700">
                    Manage organizations and their settings.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <Button asChild className="bg-emerald-600 hover:bg-emerald-700">
                      <Link to="/admin/organizations">
                        Manage Organizations <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-emerald-900">{stats.totalOrganizations}</div>
                      <p className="text-xs text-emerald-700">
                        Active organizations
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-teal-50 to-teal-100 border-teal-200 hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-teal-800">
                    <Building2 className="h-5 w-5" />
                    Buildings
                  </CardTitle>
                  <CardDescription className="text-teal-700">
                    Manage buildings and their configurations.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <Button asChild className="bg-teal-600 hover:bg-teal-700">
                      <Link to="/admin/buildings">
                        Manage Buildings <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-teal-900">{stats.totalBuildings}</div>
                      <p className="text-xs text-teal-700">
                        Total buildings
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200 hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-amber-800">
                    <KeyRound className="h-5 w-5" />
                    Connector Keys
                  </CardTitle>
                  <CardDescription className="text-amber-700">
                    Manage API keys and connectors.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button asChild className="bg-amber-600 hover:bg-amber-700">
                    <Link to="/admin/connector-keys">
                      Manage Keys <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Legacy Section */}
        <div>
          <h2 className="text-xl font-semibold mb-4 text-gray-800">Legacy Tools</h2>
          <div className="grid gap-6">
            <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200 hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-gray-800">
                  <History className="h-5 w-5" />
                  Analisi Estratto Conto Bancario
                </CardTitle>
                <CardDescription className="text-gray-700">
                  Seleziona lo stabile e carica gli estratti c/c bancari per lanciare l’analisi.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button asChild className="bg-orange-600 hover:bg-orange-700">
                  <Link to="/legacy">
                    Apri l'analisi estratti bancari<ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </CardContent>
            </Card>

            {user?.role !== 'admin' && (
              <Card className="bg-gradient-to-br from-cyan-50 to-cyan-100 border-cyan-200 hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-gray-800">
                    <Building className="h-5 w-5" />
                    I Miei Stabili
                  </CardTitle>
                  <CardDescription className="text-gray-700">
                    Sincronizzazione dati degli stabili con il tuo gestionale.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button asChild className="bg-cyan-600 hover:bg-cyan-700">
                    <Link to="/my-buildings">
                      Visualizza i Miei Stabili<ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage; 